import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { TextReveal } from './TextReveal'
import LogoScene from './LogoScene'
import Icons from './Icons'

export function Hero() {
  const heroRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate elements
      gsap.fromTo(
        '.hero-badge',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 0.2 }
      )

      gsap.fromTo(
        '.hero-buttons',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 0.8 }
      )

      gsap.fromTo(
        '.hero-stats',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 1 }
      )
    }, heroRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-radial from-[rgba(255,0,255,0.05)] via-transparent to-transparent" />

      {/* Large background title */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <h2 className="text-[12rem] md:text-[16rem] lg:text-[20rem] font-black text-[rgba(255,0,255,0.03)] leading-none select-none">
          OKX
        </h2>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div ref={contentRef} className="text-center lg:text-left">
            {/* Badge */}
            <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(255,0,255,0.1)] border border-[rgba(255,0,255,0.2)] mb-8">
              <span className="w-2 h-2 rounded-full bg-[#ff00ff] animate-pulse" />
              <span className="text-sm text-[#e0e0ff]">全球顶级安全交易平台</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <TextReveal className="text-white">
                okb官网 - 数字资产安全守护者
              </TextReveal>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-[#8080a0] mb-8 max-w-2xl mx-auto lg:mx-0">
              易欧ouyi平台采用军工级安全防护，冷热钱包分离存储，
              多重签名技术保障。okb官方网站为您提供最值得信赖的数字资产交易体验。
            </p>

            {/* Buttons */}
            <div className="hero-buttons flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
              <a
                href="https://www.jsqkmfytwpn.com/join/1p9999"
                target="_blank"
                rel="noopener"
                className="btn-primary flex items-center justify-center gap-2 text-base"
              >
                立即注册
                <Icons.ArrowRight />
              </a>
              <a
                href="https://download.prhci.com/upgradeapp/android_1P9999.apk"
                target="_blank"
                rel="noopener"
                className="btn-secondary flex items-center justify-center gap-2 text-base"
              >
                <Icons.Download />
                下载APP
              </a>
            </div>

            {/* Quick Stats */}
            <div className="hero-stats grid grid-cols-3 gap-6">
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-[#ff00ff]">5000万+</div>
                <div className="text-sm text-[#8080a0]">全球用户</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-[#00ffff]">99.9%</div>
                <div className="text-sm text-[#8080a0]">系统稳定性</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-[#ff00ff]">200+</div>
                <div className="text-sm text-[#8080a0]">安全专家</div>
              </div>
            </div>
          </div>

          {/* Right Content - 3D Logo */}
          <div className="relative h-[500px] lg:h-[600px]">
            <LogoScene />
            {/* Glow effect */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[rgba(255,0,255,0.2)] to-[rgba(0,255,255,0.2)] rounded-full blur-[120px] pointer-events-none" />
          </div>
        </div>
      </div>
    </section>
  )
}
